import { APICallError } from '@internal/ai-sdk-v5';
import { MastraError, ErrorDomain, ErrorCategory } from '../../../error';
import { getModelMethodFromAgentMethod } from '../../../llm/model/model-method-from-agent';
import type { ModelLoopStreamArgs, ModelMethodType } from '../../../llm/model/model.loop.types';
import type { MastraMemory } from '../../../memory/memory';
import type { MemoryConfigInternal } from '../../../memory/types';
import { createObservabilityContext } from '../../../observability';
import type { Span, SpanType } from '../../../observability';
import { StructuredOutputProcessor } from '../../../processors';
import type { RequestContext } from '../../../request-context';
import type { Step } from '../../../workflows/step';
import type { InnerAgentExecutionOptions } from '../../agent.types';
import type { SaveQueueManager } from '../../save-queue';
import { getModelOutputForTripwire } from '../../trip-wire';
import type { AgentMethodType } from '../../types';
import { isSupportedLanguageModel } from '../../utils';
import type { PrepareStreamRunScope } from './run-scope';
import type { AgentCapabilities, PrepareMemoryStepOutput, PrepareToolsStepOutput } from './schema';

interface MapResultsStepOptions<OUTPUT = undefined> {
  capabilities: AgentCapabilities;
  options: InnerAgentExecutionOptions<OUTPUT>;
  resourceId?: string;
  threadId?: string;
  runId: string;
  requestContext: RequestContext;
  memory?: MastraMemory;
  memoryConfig?: MemoryConfigInternal;
  agentSpan?: Span<SpanType.AGENT_RUN>;
  agentId: string;
  methodType: AgentMethodType;
  saveQueueManager?: SaveQueueManager;
  runScope: PrepareStreamRunScope<OUTPUT>;
}

export function createMapResultsStep<OUTPUT = undefined>({
  capabilities,
  options,
  resourceId,
  threadId: threadIdFromArgs,
  runId,
  requestContext,
  memory,
  memoryConfig,
  agentSpan,
  agentId,
  methodType,
  saveQueueManager,
  runScope,
}: MapResultsStepOptions<OUTPUT>): Step<
  string,
  unknown,
  {
    'prepare-tools-step': PrepareToolsStepOutput;
    'prepare-memory-step': PrepareMemoryStepOutput;
  },
  ModelLoopStreamArgs<any, OUTPUT>
>['execute'] {
  return async ({ inputData, bail, ..._observabilityContext }) => {
    const memoryData = inputData['prepare-memory-step'];

    // Class instances written to runScope by upstream steps. These never travel
    // through inputData because the evented engine JSON-serializes step outputs.
    const messageList = runScope.messageList!;
    const convertedTools = runScope.convertedTools;

    let threadCreatedByStep = false;

    const result = {
      ...options,
      agentId,
      tools: convertedTools,
      runId,
      temperature: options.modelSettings?.temperature,
      toolChoice: options.toolChoice,
      thread: memoryData.thread,
      threadId: memoryData.thread?.id ?? threadIdFromArgs,
      resourceId,
      requestContext,
      messageList,
      onStepFinish: async (props: any) => {
        // When OM is enabled saving per step corrupts things because OM handles its own saving
        const shouldSavePerStep = options.savePerStep && !memoryConfig?.observationalMemory;
        if (shouldSavePerStep && !memoryConfig?.readOnly) {
          if (!memoryData.threadExists && !threadCreatedByStep && memory && memoryData.thread) {
            await memory.createThread({
              threadId: memoryData.thread?.id,
              title: memoryData.thread?.title,
              metadata: memoryData.thread?.metadata,
              resourceId: memoryData.thread?.resourceId,
              memoryConfig,
            });

            threadCreatedByStep = true;
          }

          if (saveQueueManager && memoryData.thread?.id) {
            await saveQueueManager.flushMessages(messageList, memoryData.thread.id, memoryConfig);
          }
        }

        return options.onStepFinish?.({ ...props, runId });
      },
      ...(memoryData.tripwire && {
        tripwire: memoryData.tripwire,
      }),
    };

    // Check for tripwire and return early if triggered
    if (result.tripwire) {
      try {
        const agentModel = await capabilities.getModel({ requestContext: result.requestContext! });

        if (!isSupportedLanguageModel(agentModel)) {
          throw new MastraError({
            id: 'MAP_RESULTS_STEP_UNSUPPORTED_MODEL',
            domain: ErrorDomain.AGENT,
            category: ErrorCategory.USER,
            text: 'Tripwire handling requires a v2/v3 model',
          });
        }

        const modelOutput = await getModelOutputForTripwire<OUTPUT>({
          tripwire: memoryData.tripwire!,
          runId,
          ...createObservabilityContext({ currentSpan: agentSpan }),
          options: options,
          model: agentModel,
          messageList,
        });

        // End agent span with tripwire information after fallback completes
        agentSpan?.end({
          output: { tripwire: memoryData.tripwire },
          attributes: {
            tripwireAbort: {
              reason: memoryData.tripwire?.reason,
              processorId: memoryData.tripwire?.processorId,
              retry: memoryData.tripwire?.retry,
              metadata: memoryData.tripwire?.metadata,
            },
          },
        });

        return bail(modelOutput);
      } catch (error) {
        // End agent span with error and tripwire context so failures aren't masked
        agentSpan?.error({
          error: error as Error,
          endSpan: true,
          attributes: {
            tripwireAbort: {
              reason: memoryData.tripwire?.reason,
              processorId: memoryData.tripwire?.processorId,
              retry: memoryData.tripwire?.retry,
              metadata: memoryData.tripwire?.metadata,
            },
          },
        });
        throw error;
      }
    }

    // Resolve output processors - overrides replace user-configured but auto-derived (memory) are kept
    let effectiveOutputProcessors = capabilities.outputProcessors
      ? typeof capabilities.outputProcessors === 'function'
        ? await capabilities.outputProcessors({
            requestContext: result.requestContext!,
            overrides: options.outputProcessors,
          })
        : options.outputProcessors || capabilities.outputProcessors
      : options.outputProcessors || [];

    // Handle structuredOutput option by creating an StructuredOutputProcessor
    // Only create the processor if a model is explicitly provided
    if (options.structuredOutput?.model) {
      const structuredProcessor = new StructuredOutputProcessor({
        ...options.structuredOutput,
        logger: capabilities.logger,
      });
      if (capabilities.mastra) {
        structuredProcessor.__registerMastra(capabilities.mastra);
      }
      if (options.structuredOutput.useAgent) {
        structuredProcessor.setAgent(capabilities.agent);
      }
      effectiveOutputProcessors = effectiveOutputProcessors
        ? [...effectiveOutputProcessors, structuredProcessor]
        : [structuredProcessor];
    }

    // Resolve input processors - overrides replace user-configured but auto-derived (memory, skills) are kept
    const effectiveInputProcessors = capabilities.inputProcessors
      ? typeof capabilities.inputProcessors === 'function'
        ? await capabilities.inputProcessors({
            requestContext: result.requestContext!,
            overrides: options.inputProcessors,
          })
        : options.inputProcessors || capabilities.inputProcessors
      : options.inputProcessors || [];

    const effectiveLLMRequestInputProcessors = capabilities.llmRequestInputProcessors
      ? typeof capabilities.llmRequestInputProcessors === 'function'
        ? await capabilities.llmRequestInputProcessors({
            requestContext: result.requestContext!,
            overrides: options.inputProcessors,
          })
        : options.inputProcessors || capabilities.llmRequestInputProcessors
      : effectiveInputProcessors;

    // Resolve error processors
    const effectiveErrorProcessors = capabilities.errorProcessors
      ? typeof capabilities.errorProcessors === 'function'
        ? await capabilities.errorProcessors({
            requestContext: result.requestContext!,
            overrides: options.errorProcessors,
          })
        : options.errorProcessors || capabilities.errorProcessors
      : options.errorProcessors || [];

    const modelMethodType: ModelMethodType = getModelMethodFromAgentMethod(methodType);

    const loopOptions = {
      methodType: modelMethodType,
      agentId,
      requestContext: result.requestContext!,
      actor: options.actor,
      ...createObservabilityContext({ currentSpan: agentSpan }),
      runId,
      toolChoice: result.toolChoice,
      tools: result.tools,
      resourceId: result.resourceId,
      threadId: result.threadId,
      stopWhen: result.stopWhen,
      maxSteps: result.maxSteps,
      providerOptions: result.providerOptions,
      includeRawChunks: options.includeRawChunks,
      options: {
        ...(options.prepareStep && { prepareStep: options.prepareStep }),
        onFinish: async (payload: any) => {
          if (payload.finishReason === 'error') {
            const provider = payload.model?.provider;
            const modelId = payload.model?.modelId;
            const error =
              payload.error instanceof Error
                ? payload.error
                : new MastraError(
                    {
                      id: 'AGENT_STREAM_ERROR',
                      text:
                        payload.error == null
                          ? 'Agent stream finished with finishReason "error" but no error payload was provided'
                          : undefined,
                      domain: ErrorDomain.AGENT,
                      category: ErrorCategory.SYSTEM,
                      details: {
                        runId,
                        ...(provider && { provider }),
                        ...(modelId && { modelId }),
                      },
                    },
                    payload.error,
                  );
            const isUpstreamError = APICallError.isInstance(error);

            if (isUpstreamError) {
              capabilities.logger.error('Upstream LLM API error', {
                error,
                runId,
                ...(provider && { provider }),
                ...(modelId && { modelId }),
              });
            } else {
              capabilities.logger.error('Error in agent stream', {
                error,
                runId,
                ...(provider && { provider }),
                ...(modelId && { modelId }),
              });
            }

            // End the AGENT_RUN span so the trace is exported.
            // Without this, the span is orphaned and exporters that wait
            // for the root span to end (e.g. Datadog) never emit the trace.
            agentSpan?.error({ error, endSpan: true });
            return;
          }

          if (payload.finishReason === 'suspended') {
            agentSpan?.end({
              output: {
                status: 'suspended',
                reason: payload.suspendReason,
                toolName: payload.toolName,
                toolCallId: payload.toolCallId,
              },
            });
            return;
          }

          if (payload.finishReason === 'aborted') {
            agentSpan?.end({
              output: {
                status: 'aborted',
                reason: 'abort',
              },
            });
            return;
          }

          // Skip memory persistence when the abort signal has fired.
          // The LLM response may have continued after the caller disconnected,
          // and we should not persist a partial or full response for an aborted request.
          const aborted = options.abortSignal?.aborted;

          if (!aborted) {
            try {
              const outputText =
                options.structuredOutput?.schema && payload.object != null
                  ? JSON.stringify(payload.object)
                  : (payload.text ?? '');

              await capabilities.executeOnFinish({
                result: payload,
                outputText,
                thread: result.thread,
                threadId: result.threadId,
                readOnlyMemory: memoryConfig?.readOnly,
                resourceId,
                memoryConfig,
                requestContext,
                agentSpan: agentSpan,
                runId,
                messageList,
                threadExists: memoryData.threadExists,
                structuredOutput: !!options.structuredOutput?.schema,
                overrideScorers: options.scorers,
              });
            } catch (e) {
              capabilities.logger.error('Error saving memory on finish', {
                error: e,
                runId,
              });

              const spanError =
                e instanceof Error
                  ? e
                  : new MastraError(
                      {
                        id: 'AGENT_ON_FINISH_ERROR',
                        domain: ErrorDomain.AGENT,
                        category: ErrorCategory.SYSTEM,
                        details: { runId },
                      },
                      e,
                    );

              agentSpan?.error({ error: spanError, endSpan: true });
            }
          } else {
            agentSpan?.end();
          }

          await options?.onFinish?.({
            ...payload,
            runId,
            messages: messageList.get.response.aiV5.model(),
            usage: payload.usage,
            totalUsage: payload.totalUsage,
          });
        },
        onStepFinish: result.onStepFinish,
        onChunk: options.onChunk,
        onError: options.onError,
        onAbort: options.onAbort,
        abortSignal: options.abortSignal,
      },
      activeTools: options.activeTools,
      structuredOutput: options.structuredOutput,
      inputProcessors: effectiveInputProcessors,
      llmRequestInputProcessors: effectiveLLMRequestInputProcessors,
      outputProcessors: effectiveOutputProcessors,
      errorProcessors: effectiveErrorProcessors,
      modelSettings: {
        ...(options.modelSettings || {}),
      },
      messageList,
      initialSignalEchoes: runScope.initialSignalEchoes,
      maxProcessorRetries: options.maxProcessorRetries,
      // IsTaskComplete scoring for supervisor patterns
      isTaskComplete: options.isTaskComplete,
      // Native goal config (agent-level): the in-loop goal step judges the
      // thread's active objective each qualifying iteration.
      goal: capabilities.agent.__getGoalConfig(),
      // Iteration hook for supervisor patterns
      onIterationComplete: options.onIterationComplete,
      processorStates: runScope.processorStates,
    };

    // Park the assembled (class-instance- and closure-laden) options on the
    // factory closure's runScope. stream-step reads from here; the workflow
    // engine never sees these non-JSON-safe refs in step inputs/outputs.
    runScope.loopOptions = loopOptions;

    return loopOptions;
  };
}
